import React, { useEffect, useRef, useState, useCallback } from 'react';
import './CustomCursor.css';

const CustomCursor = () => {
    const dotRef = useRef(null);
    const ringRef = useRef(null);
    const [isHovering, setIsHovering] = useState(false);
    const [isClicking, setIsClicking] = useState(false);
    const [bursts, setBursts] = useState([]);
    const [isVisible, setIsVisible] = useState(false);
    const mousePos = useRef({ x: -100, y: -100 });
    const ringPos = useRef({ x: -100, y: -100 });
    const rafId = useRef(null);

    // Check if touch device
    const isTouchDevice = typeof window !== 'undefined' &&
        ('ontouchstart' in window || navigator.maxTouchPoints > 0);

    const animateRing = useCallback(() => {
        // Spring-based lag for ring
        const dx = mousePos.current.x - ringPos.current.x;
        const dy = mousePos.current.y - ringPos.current.y;
        ringPos.current.x += dx * 0.15;
        ringPos.current.y += dy * 0.15;

        if (dotRef.current) {
            dotRef.current.style.transform = `translate(${mousePos.current.x - 4}px, ${mousePos.current.y - 4}px)`;
        }
        if (ringRef.current) {
            const size = isHovering ? 60 : 40;
            ringRef.current.style.transform = `translate(${ringPos.current.x - size / 2}px, ${ringPos.current.y - size / 2}px)`;
        }

        rafId.current = requestAnimationFrame(animateRing);
    }, [isHovering]);

    useEffect(() => {
        if (isTouchDevice) return;

        const handleMouseMove = (e) => {
            mousePos.current = { x: e.clientX, y: e.clientY };
            if (!isVisible) setIsVisible(true);
        };

        const handleMouseOver = (e) => {
            const target = e.target;
            const isInteractive = target.matches(
                'a, button, input, textarea, select, [role="button"], .btn, .social-link, .navbar-link, .card, .service-card, .skill-card, .portfolio-card, .category-btn, .theme-toggle, .navbar-toggle, .navbar-brand'
            );
            setIsHovering(isInteractive);
        };

        const handleMouseDown = () => {
            setIsClicking(true);
            // Create burst
            setBursts(prev => [...prev, {
                id: Date.now(),
                x: mousePos.current.x,
                y: mousePos.current.y
            }]);
            // Remove burst after animation
            setTimeout(() => {
                setBursts(prev => prev.slice(1));
            }, 500);
        };

        const handleMouseUp = () => setIsClicking(false);

        const handleMouseLeave = () => setIsVisible(false);
        const handleMouseEnter = () => setIsVisible(true);

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseover', handleMouseOver);
        window.addEventListener('mousedown', handleMouseDown);
        window.addEventListener('mouseup', handleMouseUp);
        document.documentElement.addEventListener('mouseleave', handleMouseLeave);
        document.documentElement.addEventListener('mouseenter', handleMouseEnter);

        rafId.current = requestAnimationFrame(animateRing);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseover', handleMouseOver);
            window.removeEventListener('mousedown', handleMouseDown);
            window.removeEventListener('mouseup', handleMouseUp);
            document.documentElement.removeEventListener('mouseleave', handleMouseLeave);
            document.documentElement.removeEventListener('mouseenter', handleMouseEnter);
            if (rafId.current) cancelAnimationFrame(rafId.current);
        };
    }, [isTouchDevice, isVisible, animateRing]);

    if (isTouchDevice) return null;

    return (
        <>
            <div
                ref={dotRef}
                className={`custom-cursor-dot ${isHovering ? 'hovering' : ''}`}
                style={{ opacity: isVisible ? 1 : 0 }}
            />
            <div
                ref={ringRef}
                className={`custom-cursor-ring ${isHovering ? 'hovering' : ''} ${isClicking ? 'clicking' : ''}`}
                style={{ opacity: isVisible ? 1 : 0 }}
            />
            {bursts.map(burst => (
                <div
                    key={burst.id}
                    className="cursor-click-burst"
                    style={{ left: burst.x, top: burst.y }}
                />
            ))}
        </>
    );
};

export default CustomCursor;
